import {Injectable, OnDestroy} from '@angular/core';
import {InAppBrowser, InAppBrowserOptions} from '@ionic-native/in-app-browser/ngx';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {EventsService} from './events.service';
import {AlertController, NavController, Platform} from '@ionic/angular';
import {Subscription} from 'rxjs';
import {AuthService} from './auth.service';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../environments/environment';

declare var navigator: any;

@Injectable({
    providedIn: 'root'
})
export class NavService implements OnDestroy {

    subscriptions: Subscription[] = [];

    public unsavedData = false;

    constructor(
        private router: Router,
        private event: EventsService,
        private http: HttpClient,
        private iab: InAppBrowser,
        private platform: Platform,
        private navCtrl: NavController,
        private authService: AuthService,
        private alertController: AlertController,
        private translate: TranslateService,
    ) {
        this.subscriptions.push(
            this.event.subscribe('form:changed', () => this.unsavedData = true)
        );
        this.subscriptions.push(
            this.event.subscribe('form:saved', () => this.unsavedData = false)
        );
    }

    public addToken(url: string) {
        if (this.authService.isLoggedin()
            && url.indexOf(environment.adminUrl) >= 0
            && url.indexOf(environment.adminUrl + '/uploads/') < 0
            && url.indexOf('report360_form') < 0) {
            if (url.indexOf('?') < 0) {
                url = url + '?token=' + this.authService.getToken();
            } else {
                url = url + '&token=' + this.authService.getToken();
            }
        }
        return url;
    }

    public toLink(url: string, target: string = '_system') {
        if (url !== '') {
            url = this.addToken(url);

            const options: InAppBrowserOptions = {
                location: 'yes',
                clearcache: 'yes',
                toolbar: 'yes'
            };
            this.iab.create(url, target, options);
        }
    }

    public back() {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.backAndClose();
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.backAndClose();
        }
    }

    public action(method: () => void) {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        method();
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            method();
        }
    }

    private backAndClose() {
        if (this.platform.is('android')) {
            if (this.router.isActive('/home', true)
                || this.router.isActive('/login', true)) {
                navigator['app'].exitApp();
            }
        }
        this.navCtrl.back();
    }

    public navigateBack(commands: any[]) {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.navCtrl.navigateBack(commands);
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.navCtrl.navigateBack(commands);
        }
    }

    public navigateHome() {
        this.navigateRoot(this.getHomeCommand());
    }

    public getHomeCommand() {
        if (this.authService.getUserCompanyDashboard() === 'multijourneys' && this.authService.getUserType() !== 'coach') {
            return ['/home-multi'];
        } else if (this.authService.getUserCompanyDashboard() === 'inspire' && this.authService.getUserType() !== 'coach') {
            return ['/home-inspire'];
        } else {
            return ['/home'];
        }
    }

    public navigateRoot(commands: any[]) {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.navCtrl.navigateRoot(commands);
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.navCtrl.navigateRoot(commands);
        }
    }

    public navigate(commands: any[]) {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.router.navigate(commands);
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.router.navigate(commands);
        }
    }

    public navigateByUrl(url: string) {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.router.navigateByUrl(url);
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.router.navigateByUrl(url);
        }
    }

    generateUrl(route: any) {
        const urlTree = this.router.createUrlTree(route);
        return this.router.serializeUrl(urlTree);
    }

    public logout() {
        if (this.unsavedData) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__ALERT_UNSAVED_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__ALERT_UNSAVED_BUTTON_LEAVE__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.unsavedData = false;
                        this.authService.logout();
                    }
                }, this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.authService.logout();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(i => i.unsubscribe());
    }
}

